/**
 * Dynamic Content Card JavaScript
 * Handles interactive functionality and theme integration
 */

class DynamicContentCard {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.init();
  }

  init() {
    this.applyThemeSettings();
    this.setupImageLazyLoading();
    this.setupProductTracking();
    this.setupAccessibility();
    this.setupResizeObserver();
    
    // Listen for theme editor updates
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.sectionId) {
        this.refresh();
      }
    });
    
    document.addEventListener('shopify:section:reorder', (event) => {
      if (event.detail.sectionId === this.sectionId) {
        this.refresh();
      }
    });
    
    document.addEventListener('shopify:block:select', (event) => {
      if (event.target.closest(`#dynamic-content-card-${this.sectionId}`)) {
        this.highlightProduct(event.target);
      }
    });
    
    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.target.closest(`#dynamic-content-card-${this.sectionId}`)) {
        this.removeHighlight(event.target);
      }
    });
  }

  applyThemeSettings() {
    const settings = this.getThemeSettings();
    
    // Apply custom CSS properties from section settings
    if (settings.card_height) {
      this.element.style.setProperty('--card-height', `${settings.card_height}px`);
    }
    
    if (settings.background_color) {
      this.element.style.setProperty('--card-bg', settings.background_color);
    }
    
    if (settings.text_color) {
      this.element.style.setProperty('--card-text', settings.text_color);
    }
    
    if (settings.button_color) {
      this.element.style.setProperty('--button-bg', settings.button_color);
    }
    
    if (settings.button_text_color) {
      this.element.style.setProperty('--button-text', settings.button_text_color);
    }
  }

  getThemeSettings() {
    // In a real Shopify environment, these would come from the section settings
    // For now, we'll extract from data attributes or CSS custom properties
    const style = getComputedStyle(this.element);
    
    return {
      card_height: parseInt(style.getPropertyValue('--card-height')) || 400,
      background_color: style.getPropertyValue('--card-bg') || '#ffffff',
      text_color: style.getPropertyValue('--card-text') || '#000000',
      button_color: style.getPropertyValue('--button-bg') || '#000000',
      button_text_color: style.getPropertyValue('--button-text') || '#ffffff'
    };
  }

  setupImageLazyLoading() {
    const images = this.element.querySelectorAll('img[loading="lazy"]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            observer.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      images.forEach(img => this.loadImage(img));
    }
  }

  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
    
    img.addEventListener('load', () => {
      img.style.opacity = '1';
    });
    
    img.addEventListener('error', () => {
      this.handleImageError(img);
    });
  }

  handleImageError(img) {
    const placeholder = img.closest('.card-image-container, .product-image').querySelector('.card-image-placeholder, .product-image-placeholder');
    if (placeholder) {
      placeholder.style.display = 'flex';
      img.style.display = 'none';
    }
  }

  setupProductTracking() {
    const productItems = this.element.querySelectorAll('.product-item');
    
    productItems.forEach((item, index) => {
      const shopButton = item.querySelector('.shop-button');
      
      if (shopButton) {
        shopButton.addEventListener('click', (e) => {
          this.trackProductClick(e, item, index);
        });
        
        // Add keyboard support
        shopButton.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            shopButton.click();
          }
        });
      }
    });
  }

  trackProductClick(event, productItem, index) {
    // Analytics tracking - can be extended to integrate with Google Analytics, Shopify Analytics, etc.
    const productName = productItem.querySelector('.product-name')?.textContent;
    const productPrice = productItem.querySelector('.price-regular, .price-sale')?.textContent;
    
    // Send to analytics
    if (window.gtag) {
      gtag('event', 'dynamic_card_product_click', {
        'custom_map': {
          'dimension1': this.sectionId,
          'dimension2': productName,
          'dimension3': index
        }
      });
    }
    
    // Shopify Analytics (if available)
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.track('Dynamic Card Product Click', {
        section_id: this.sectionId,
        product_name: productName,
        product_position: index,
        product_price: productPrice
      });
    }
    
    console.log(`Product clicked: ${productName} at position ${index} in section ${this.sectionId}`);
  }

  setupAccessibility() {
    // Add ARIA labels and roles for better accessibility
    const productsList = this.element.querySelector('.products-list');
    if (productsList) {
      productsList.setAttribute('role', 'list');
      productsList.setAttribute('aria-label', 'Featured products');
    }
    
    const productItems = this.element.querySelectorAll('.product-item');
    productItems.forEach((item, index) => {
      item.setAttribute('role', 'listitem');
      
      const shopButton = item.querySelector('.shop-button');
      if (shopButton) {
        const productName = item.querySelector('.product-name')?.textContent;
        shopButton.setAttribute('aria-label', `Shop ${productName || 'product'}`);
      }
    });
    
    // Add skip link for keyboard users
    const skipLink = document.createElement('a');
    skipLink.href = `#dynamic-content-card-${this.sectionId}`;
    skipLink.textContent = `Skip to ${this.element.querySelector('.card-title')?.textContent || 'content'} section`;
    skipLink.className = 'skip-link visually-hidden';
    skipLink.addEventListener('focus', () => skipLink.classList.remove('visually-hidden'));
    skipLink.addEventListener('blur', () => skipLink.classList.add('visually-hidden'));
    
    this.element.parentNode.insertBefore(skipLink, this.element);
  }

  setupResizeObserver() {
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(entry => {
          const cardWidth = entry.contentRect.width;
          
          // Adjust layout based on container width
          if (cardWidth < 600) {
            this.element.classList.add('compact-layout');
          } else {
            this.element.classList.remove('compact-layout');
          }
          
          // Update products list height for mobile
          this.updateProductsListHeight();
        });
      });
      
      resizeObserver.observe(this.element);
    }
  }

  updateProductsListHeight() {
    const productsList = this.element.querySelector('.products-list');
    const textSection = this.element.querySelector('.card-text-section');
    
    if (productsList && textSection && window.innerWidth <= 768) {
      // On mobile, ensure products list doesn't overflow
      const availableHeight = this.element.offsetHeight - textSection.offsetHeight - 100; // 100px padding
      productsList.style.maxHeight = `${Math.max(150, availableHeight)}px`;
    } else if (productsList) {
      productsList.style.maxHeight = '';
    }
  }

  highlightProduct(productElement) {
    // Highlight product when selected in theme editor
    const productItem = productElement.closest('.product-item');
    if (productItem) {
      productItem.style.outline = '2px solid #007cba';
      productItem.style.outlineOffset = '2px';
      productItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  removeHighlight(productElement) {
    // Remove highlight when deselected in theme editor
    const productItem = productElement.closest('.product-item');
    if (productItem) {
      productItem.style.outline = '';
      productItem.style.outlineOffset = '';
    }
  }

  refresh() {
    // Refresh the component when settings change
    this.applyThemeSettings();
    this.setupImageLazyLoading();
    this.setupProductTracking();
    this.setupAccessibility();
    this.updateProductsListHeight();
  }

  destroy() {
    // Cleanup when component is removed
    const skipLink = this.element.parentNode.querySelector('.skip-link');
    if (skipLink) {
      skipLink.remove();
    }
  }
}

// Auto-initialize all dynamic content cards on page load
document.addEventListener('DOMContentLoaded', function() {
  initializeDynamicContentCards();
});

// Initialize cards when sections are loaded via AJAX
document.addEventListener('shopify:section:load', function(event) {
  const sectionElement = event.target;
  const cards = sectionElement.querySelectorAll('.dynamic-content-card');
  cards.forEach(card => new DynamicContentCard(card));
});

function initializeDynamicContentCards() {
  const cards = document.querySelectorAll('.dynamic-content-card');
  cards.forEach(card => {
    if (!card.dataset.initialized) {
      new DynamicContentCard(card);
      card.dataset.initialized = 'true';
    }
  });
}

// Utility functions for theme developers
window.DynamicContentCard = {
  init: initializeDynamicContentCards,
  create: (element) => new DynamicContentCard(element)
};

// Add some utility CSS for accessibility
const utilityCSS = `
  .visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
  
  .skip-link {
    position: absolute;
    top: -40px;
    left: 6px;
    background: #000;
    color: #fff;
    padding: 8px;
    text-decoration: none;
    border-radius: 4px;
    z-index: 1000;
    transition: top 0.3s;
  }
  
  .skip-link:focus {
    top: 6px;
  }
  
  .compact-layout .product-item {
    flex-wrap: wrap;
  }
  
  .compact-layout .product-details {
    flex: 1 1 100%;
    margin-top: 8px;
  }
`;

// Inject utility CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = utilityCSS;
document.head.appendChild(styleSheet);